import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './accommodation-type.reducer';

export const AccommodationType = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const accommodationTypeList = useAppSelector(state => state.accommodationType.entities);
  const loading = useAppSelector(state => state.accommodationType.loading);
  const totalItems = useAppSelector(state => state.accommodationType.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="accommodation-type-heading" data-cy="AccommodationTypeHeading">
        <Translate contentKey="apiLocationApp.accommodationType.home.title">Accommodation Types</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.accommodationType.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/accommodation-type/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.accommodationType.home.createLabel">Create new Accommodation Type</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {accommodationTypeList && accommodationTypeList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.accommodationType.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.accommodationType.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.accommodationType.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.accommodationType.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.accommodationType.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllHouseholds')}>
                  <Translate contentKey="apiLocationApp.accommodationType.totalAllHouseholds">Total All Households</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllHouseholds')} />
                </th>
                <th className="hand" onClick={sort('detached')}>
                  <Translate contentKey="apiLocationApp.accommodationType.detached">Detached</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('detached')} />
                </th>
                <th className="hand" onClick={sort('semiDetached')}>
                  <Translate contentKey="apiLocationApp.accommodationType.semiDetached">Semi Detached</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('semiDetached')} />
                </th>
                <th className="hand" onClick={sort('terraced')}>
                  <Translate contentKey="apiLocationApp.accommodationType.terraced">Terraced</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('terraced')} />
                </th>
                <th className="hand" onClick={sort('purposeBuiltBlockFlats')}>
                  <Translate contentKey="apiLocationApp.accommodationType.purposeBuiltBlockFlats">Purpose Built Block Flats</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('purposeBuiltBlockFlats')} />
                </th>
                <th className="hand" onClick={sort('convertedSharedHouse')}>
                  <Translate contentKey="apiLocationApp.accommodationType.convertedSharedHouse">Converted Shared House</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('convertedSharedHouse')} />
                </th>
                <th className="hand" onClick={sort('convertedBuilding')}>
                  <Translate contentKey="apiLocationApp.accommodationType.convertedBuilding">Converted Building</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('convertedBuilding')} />
                </th>
                <th className="hand" onClick={sort('commercialBuilding')}>
                  <Translate contentKey="apiLocationApp.accommodationType.commercialBuilding">Commercial Building</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('commercialBuilding')} />
                </th>
                <th className="hand" onClick={sort('caravanMobile')}>
                  <Translate contentKey="apiLocationApp.accommodationType.caravanMobile">Caravan Mobile</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('caravanMobile')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {accommodationTypeList.map((accommodationType, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/accommodation-type/${accommodationType.id}`} color="link" size="sm">
                      {accommodationType.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${accommodationType.geographicLevel}`} />
                  </td>
                  <td>{accommodationType.date}</td>
                  <td>{accommodationType.geography}</td>
                  <td>{accommodationType.geographyCode}</td>
                  <td>{accommodationType.totalAllHouseholds}</td>
                  <td>{accommodationType.detached}</td>
                  <td>{accommodationType.semiDetached}</td>
                  <td>{accommodationType.terraced}</td>
                  <td>{accommodationType.purposeBuiltBlockFlats}</td>
                  <td>{accommodationType.convertedSharedHouse}</td>
                  <td>{accommodationType.convertedBuilding}</td>
                  <td>{accommodationType.commercialBuilding}</td>
                  <td>{accommodationType.caravanMobile}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/accommodation-type/${accommodationType.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/accommodation-type/${accommodationType.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/accommodation-type/${accommodationType.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.accommodationType.home.notFound">No Accommodation Types found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={accommodationTypeList && accommodationTypeList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default AccommodationType;
